import BlogPage from 'src/views/Marketing/Blog/components/BlogPage';
import Footer from 'src/views/Marketing/components/Footer';
import NavBar from 'src/views/Marketing/components/NavBar';
import { ScrollView, StyleSheet } from 'react-native';
import * as Constants from 'src/constants';

export default function Page() {
  return (
    <ScrollView style={styles.scrollView}>
      <NavBar />
      <BlogPage
        author='OneShop Team'
        dateTime='2022-11-09'
        tag='Poshmark'
        title='Poshmark Listing Templates: The Secret to Top Sales'
      >
        <p>
          While a bright and crisp cover photo (Pro tip: Shoot your items with natural light!) and a comprehensive title
          – one that includes keywords like the brand name, name of the item or style number, and in some instances, the
          size and color – are what immediately entices potential buyers, they could easily lose interest with a
          lackluster listing that doesn't do anything to convince them to end their browsing journey with a purchase
          from your closet.
        </p>
        <p>
          Whether you're a Poshmark newbie or a seasoned seller, having a winning listing template in your back pocket
          is essential. It serves as a formula or mental checklist to ensure that each item in your closet is equipped
          to hold its own in a sea of similar products.
        </p>

        <h3>Why use a listing template?</h3>
        <p>
          If you're new to the platform, you may be second-guessing which details to include. On the other hand, if
          you're an experienced Poshmark seller who posts listings regularly, the temptation to get it over with less
          time may cause you to leave out important keywords in the process inadvertently. A go-to listing template
          would serve you – and your potential buyers well in either scenario.
        </p>
        <p>
          Let's be more specific. For instance, a buyer searching for a pair of Swedish Hasbeens clogs "liked" your item
          but decided to go with the other seller in the search results. This is because that seller mentioned the exact
          color ("Midnight Blue," which can easily be mistaken as "Black" from photos alone) and took the extra step to
          include the platform height and heel height, both of which are details that are not easy to ascertain on
          screen. A great listing template is complete, clear, and not confusing to potential buyers.
        </p>

        <h3>What should I include in a listing template?</h3>
        <p>
          Here's a fun exercise: Pretend you're a shopper and search Poshmark for items you're interested in. What kinds
          of listing templates entice you enough to make a purchase? We are not mindreaders, but we know which ones have
          caught your attention. Here, we show you the simple anatomy of a listing template that translates to a sale.
        </p>
        <ul>
          <li>
            <strong>A Short Description</strong>
            <p>
              A couple of concise, descriptive sentences are key in setting the stage for making your item irresistible.
              Let your item's unique features shine or whet potential buyers' appetite with its styling possibilities.
              For instance, they already know they're looking at photos of a pleated Lululemon tennis skirt. Amplify the
              excitement by mentioning how it looks gorgeous enough to wear off-court with a pair of ballerina flats or
              kitten heels. Oh, and the pleats shimmer a bit as you walk or when the wind rustles.
            </p>
            <p>
              However, at the risk of sounding phony, never invent features that don't exist just for the sake of
              writing something whimsical. Also, refrain from going overboard with a long paragraph (keep it under 500
              characters) and too many adjectives. Instead, use the space to mention details that may not be readily
              obvious from the photos, such as the type of wash for a pair of jeans, a secret zipper, gold hardware, an
              extra button, and dust bag inclusion.
            </p>
            <p>
              Your description is a personalized chance to communicate your listing with the buyer. Since they are
              viewing your listing already, they are interested in it. All they need is a little nudge here, a push
              there; Tease their imagination with possibilities of creating new outfits. Position your item as a
              terrific addition to their wardrobe. Win them over with an A+ description!
            </p>
          </li>
          <li>
            <strong>Accurate size measurements</strong>
            <p>
              Size is crucial information in any listing and even a non-negotiable factor for a potential buyer. As a
              Poshmark seller, take advantage of the space allotted for the listing template to mention any size details
              specific to the brand, as sizes across different brands can vary greatly.
            </p>
            <p>
              Serious buyers also appreciate sellers who invest time in including exact item measurements, especially
              for clothes and bags. Lay-flat the item and measure what is relevant – waist, bust, length from shoulder
              to hem, or even shoulder sleeve length. You will probably get inquiries related to these anyway, so it
              would be better to provide upfront information. It helps in expediting a shopper's decision to get the
              item or not.
            </p>
          </li>
          <li>
            <strong>Material and item condition.</strong>
            <p>
              It is good to assume that every potential buyer is meticulous. And with this, remember to indicate if the
              item is new with tags (NWT), new without tags (NWOT), or used but in excellent condition. For used items,
              disclose any imperfections, however small (and say so if these are unnoticeable) for transparency. Details
              that indicate that the seller is a non-smoker or doesn't own a pet may also help a shopper gauge the
              quality of the item based on the environment it will be coming from.
            </p>
            <p>
              Another important detail is the material. If this information is not indicated on the wash care tag, do
              your homework, and Google-search the item on its brand's website. For example, a material may seem like
              pure linen when touched but is actually partly made of cotton or other fiber, so it is best to check.
              While you're at it, and if you are sure, it is also good to mention keywords like "waterproof" or
              "water-resistant" for items such as a jacket or a backpack.
            </p>
          </li>
        </ul>

        <h3>Examples: The Good and The Bad</h3>
        <p>
          Before you dive right in and craft your engaging Poshmark listing template, we thought you'd like to take
          inspiration from a few samples of both good and bad ones for a clearer grasp of what to do and what to avoid!
        </p>

        <p>
          <strong>Good Listing Example</strong>
        </p>
        <p>
          "Elevate your next workout session with this ribbed crop sports bra that shows off your form and lends support
          where you need it. Its neutral color lets you easily mix and match it with what you have on rotation, or wear
          a blazer over it for on-trend casual chic. NWT, Padded, Slips on Beige Polyester 78%, Spandex 22% US Size 2;
          Underband 23" - 24"
        </p>
        <p>
          <strong>What makes it good?</strong>
        </p>
        <ul>
          <li>Highlights what makes the item functional and versatile.</li>
          <li>Material composition</li>
          <li>Measurement</li>
        </ul>
        <p>
          <strong>Bad Listing Example</strong>
        </p>
        <p>"Black Leather Crossbody with Pink Flamingo NWT Retails at $250."</p>
        <p>
          <strong>What makes it bad?</strong>
        </p>
        <ul>
          <li>No mention of its standout features</li>
          <li>No measurement</li>
          <li>No leather type specification (Is it cow or calfskin leather or vegan leather?)</li>
        </ul>

        <h3>In Summary</h3>
        <p>
          As with other aspects of a Poshmark closet, a well-crafted listing template is part of a seller's branding and
          reputation. Those few strings of words could actually reflect how professional and passionate you are about
          what you're selling, be it a designer bag or a sleepwear robe. Whether you're selling on the platform only as
          a side hustle for extra money or rely on it heavily as a regular stream of income, putting in the extra
          minutes to come up with a description and details that cover the essentials mentioned above is definitely
          worth it.
        </p>

        <h3>﻿Automation can help you!</h3>
        <p>
          Becoming a successful seller on this platform involves being hands-on with the nitty-gritty. It does take
          time, but the good part is that there are ways to nurture your business without necessarily being on it 24/7.
          You can, for instance, turn to OneShop to scale up your business.{' '}
        </p>
        <p>
          Consider this reliable Poshmark bot as your hardworking silent partner that takes care of the time-consuming
          but highly rewarding parts of running your business. OneShop can help you share your closet (it can do up to
          28,000 shares a week without the risk of landing in Share Jail) to boost visibility, follow hundreds of users,
          return shares and follows, and send offers to likers automatically.
        </p>
        <p>
          What's more, OneShop also helps you increase your conversions by making your listings visible to a broader
          audience by cross-listing to other marketplaces such as Mercari, Tradesy, Depop, and other reselling
          platforms. In addition, dealing with incidents when multiple buyers buy a single item at once on multiple
          marketplaces will not be a problem anymore as OneShop automatically delists items across multiple platforms
          once they get sold on another.
        </p>
        <p>
          Furthermore, keeping track of your sales is easier with OneShop's sales tracker and goal setting feature,
          which will allow you to monitor your progress in achieving your goals.
        </p>
        <p>
          With these automated tasks off your plate, you can now focus on thinking of strategies to grow your business
          further. You will have more time to source saleable items to list and mindfully write each listing template.
        </p>
        <p>
          Try OneShop and get first-hand experience on how automation can free up your time. Start your 7-day free trial
          today by signing up <a href='https://tools.oneshop.com/signup'>here</a>.
        </p>
      </BlogPage>
      <Footer />
    </ScrollView>
  );
}

const styles = StyleSheet.create({
  scrollView: {
    flex: 1,
  },
});
